import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Copy, FileCode, Loader2, Monitor, Pencil, RefreshCw, Smartphone, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmModal from '../Common/ConfirmModal';
import Tooltip from '../Common/Tooltip';
import { fetchScheduledEmailPreview } from '../../lib/previewScheduledEmail';
import { supabase } from '../../lib/supabase';
import PostmarkTemplatePickerModal from './PostmarkTemplatePickerModal';
import {
  DEFAULT_PERSONALIZATION_CONFIG,
  parsePersonalizationConfig,
} from '../../lib/personalization';
import {
  CONTENT_MODE_OPTIONS,
  inferContentMode,
  type AutomationStepContentMode,
} from './contentMode';
import type { AutomationStepRow, PostmarkTemplate } from './types';

interface AutomationStepEditorProps {
  isOpen: boolean;
  onClose: () => void;
  automationId: string;
  /** When provided, edit mode. Otherwise create. */
  existing: AutomationStepRow | null;
  /** Suggested next position to use when creating. */
  nextPosition: number;
  onSuccess: (savedStepId?: string) => void;
  /** Ordered steps for prev/next navigation in the header. */
  allSteps?: AutomationStepRow[];
  /** Switch to another step after flushing autosave. */
  onNavigateStep?: (stepId: string) => void | Promise<void>;
  /**
   * If provided (edit mode only), shows a "Duplicate" button in the footer.
   * The parent is responsible for closing this editor and opening its own
   * duplicate confirmation modal.
   */
  onDuplicate?: () => void;
}

const DEFAULT_SUBJECT = 'Hello from the automation';
const DEFAULT_BODY = '<p>Hello!</p>';

type EditorViewport = 'edit' | 'desktop' | 'mobile';

const DEFAULT_EDITOR_VIEWPORT: EditorViewport = 'edit';
const VIEW_QUERY_KEY = 'view';
const AUTO_SAVE_DELAY_MS = 900;

function parseEditorViewport(raw: string | null): EditorViewport {
  return raw === 'desktop' || raw === 'mobile' || raw === 'edit'
    ? raw
    : DEFAULT_EDITOR_VIEWPORT;
}

function formatSupabaseError(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'message' in e) {
    const err = e as { message?: string; details?: string; hint?: string };
    return [err.message, err.details, err.hint].filter(Boolean).join(' — ') || fallback;
  }
  return e instanceof Error ? e.message : fallback;
}

type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

function serializeStepForm(state: {
  dayOffset: number;
  timeOfDay: string;
  name: string;
  subject: string;
  bodyHtml: string;
  templateAlias: string;
  contentMode: AutomationStepContentMode;
  active: boolean;
  aiPersonalizationEnabled: boolean;
  aiMinScore: number;
}): string {
  return JSON.stringify(state);
}

function readStepFormSnapshot(
  dayOffset: number,
  timeOfDay: string,
  name: string,
  subject: string,
  bodyHtml: string,
  templateAlias: string,
  contentMode: AutomationStepContentMode,
  active: boolean,
  aiPersonalizationEnabled: boolean,
  aiMinScore: number,
): string {
  return serializeStepForm({
    dayOffset,
    timeOfDay,
    name,
    subject,
    bodyHtml,
    templateAlias,
    contentMode,
    active,
    aiPersonalizationEnabled,
    aiMinScore,
  });
}

export default function AutomationStepEditor({
  isOpen,
  onClose,
  automationId,
  existing,
  nextPosition,
  onSuccess,
  allSteps,
  onNavigateStep,
  onDuplicate,
}: AutomationStepEditorProps) {
  const isEdit = !!existing;
  const [position, setPosition] = useState(nextPosition);
  const [dayOffset, setDayOffset] = useState(0);
  const [timeOfDay, setTimeOfDay] = useState('08:00');
  const [name, setName] = useState('');
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [bodyHtml, setBodyHtml] = useState(DEFAULT_BODY);
  const [templateAlias, setTemplateAlias] = useState('');
  const [contentMode, setContentMode] = useState<AutomationStepContentMode>('manual');
  const [active, setActive] = useState(true);
  const [aiPersonalizationEnabled, setAiPersonalizationEnabled] = useState(false);
  const [aiMinScore, setAiMinScore] = useState(
    DEFAULT_PERSONALIZATION_CONFIG.min_score,
  );
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [templates, setTemplates] = useState<PostmarkTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewWarning, setPreviewWarning] = useState<string | null>(null);
  const editorViewport = parseEditorViewport(searchParams.get(VIEW_QUERY_KEY));
  const setEditorViewport = useCallback(
    (viewport: EditorViewport) => {
      setSearchParams(
        (prev) => {
          const current = parseEditorViewport(prev.get(VIEW_QUERY_KEY));
          if (current === viewport) return prev;
          const next = new URLSearchParams(prev);
          if (viewport === DEFAULT_EDITOR_VIEWPORT) next.delete(VIEW_QUERY_KEY);
          else next.set(VIEW_QUERY_KEY, viewport);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const [persistedStepId, setPersistedStepId] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [formHydrated, setFormHydrated] = useState(false);
  const [pendingSendCount, setPendingSendCount] = useState<number | null>(null);
  const [pendingSendCountLoading, setPendingSendCountLoading] = useState(false);
  const [propagating, setPropagating] = useState(false);
  const [propagateConfirmOpen, setPropagateConfirmOpen] = useState(false);

  const lastSavedSnapshotRef = useRef('');
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const persistInFlightRef = useRef(false);
  const previewRequestIdRef = useRef(0);

  const stepId = existing?.id ?? persistedStepId;

  const loadPendingSendCount = useCallback(async () => {
    if (!stepId) {
      setPendingSendCount(null);
      return;
    }
    setPendingSendCountLoading(true);
    try {
      const { count, error } = await supabase
        .from('scheduled_emails')
        .select('id', { count: 'exact', head: true })
        .eq('automation_step_id', stepId)
        .eq('status', 'pending');
      if (error) throw error;
      setPendingSendCount(count ?? 0);
    } catch {
      setPendingSendCount(null);
    } finally {
      setPendingSendCountLoading(false);
    }
  }, [stepId]);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const { data, error } = await supabase.functions.invoke('postmark-templates', {
        body: { activeOnly: true, includeLayouts: true },
      });
      if (error) {
        const detail = (data as { error?: string } | null)?.error;
        throw new Error(detail || error.message);
      }
      const list = (data as { templates?: PostmarkTemplate[] } | null)?.templates ?? [];
      setTemplates(list);
    } catch (e: unknown) {
      setTemplatesError(e instanceof Error ? e.message : 'Failed to load templates');
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setFormHydrated(false);
      setPersistedStepId(null);
      setSaveStatus('idle');
      setSaveError(null);
      setPendingSendCount(null);
      setPropagateConfirmOpen(false);
      lastSavedSnapshotRef.current = '';
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      return;
    }

    setPreviewHtml('');
    setPreviewSubject('');
    setPreviewWarning(null);
    setPersistedStepId(existing?.id ?? null);
    setSaveStatus('idle');
    setSaveError(null);

    if (existing) {
      setPosition(existing.position);
      setDayOffset(existing.day_offset);
      setTimeOfDay(existing.time_of_day);
      setName(existing.name ?? '');
      setSubject(existing.subject ?? '');
      setBodyHtml(existing.body_html ?? '');
      setTemplateAlias(existing.postmark_template_alias ?? '');
      setContentMode(inferContentMode(existing));
      setActive(existing.active !== false);
      const pc = parsePersonalizationConfig(existing.personalization_config);
      setAiPersonalizationEnabled(pc.enabled);
      setAiMinScore(pc.min_score);
      lastSavedSnapshotRef.current = readStepFormSnapshot(
        existing.day_offset,
        existing.time_of_day,
        existing.name ?? '',
        existing.subject ?? '',
        existing.body_html ?? '',
        existing.postmark_template_alias ?? '',
        inferContentMode(existing),
        existing.active !== false,
        pc.enabled,
        pc.min_score,
      );
    } else {
      setPosition(nextPosition);
      setDayOffset(0);
      setTimeOfDay('08:00');
      setName('');
      setSubject(DEFAULT_SUBJECT);
      setBodyHtml(DEFAULT_BODY);
      setTemplateAlias('');
      setContentMode('manual');
      setActive(true);
      setAiPersonalizationEnabled(false);
      setAiMinScore(DEFAULT_PERSONALIZATION_CONFIG.min_score);
      lastSavedSnapshotRef.current = readStepFormSnapshot(
        0,
        '08:00',
        '',
        DEFAULT_SUBJECT,
        DEFAULT_BODY,
        '',
        'manual',
        true,
        false,
        DEFAULT_PERSONALIZATION_CONFIG.min_score,
      );
    }

    setFormHydrated(true);
  }, [isOpen, existing, nextPosition]);

  useEffect(() => {
    if (!isOpen || !stepId) return;
    void loadPendingSendCount();
  }, [isOpen, stepId, loadPendingSendCount]);

  const currentSnapshot = useMemo(
    () =>
      readStepFormSnapshot(
        dayOffset,
        timeOfDay,
        name,
        subject,
        bodyHtml,
        templateAlias,
        contentMode,
        active,
        aiPersonalizationEnabled,
        aiMinScore,
      ),
    [
      dayOffset,
      timeOfDay,
      name,
      subject,
      bodyHtml,
      templateAlias,
      contentMode,
      active,
      aiPersonalizationEnabled,
      aiMinScore,
    ],
  );

  const buildPayload = useCallback(() => {
    const subjectTrim = subject.trim();
    const bodyTrim = bodyHtml.trim();
    const aliasTrim = templateAlias.trim();

    return {
      automation_id: automationId,
      position,
      day_offset: dayOffset,
      time_of_day: timeOfDay,
      name: name.trim() || null,
      content_mode: contentMode,
      subject: contentMode === 'template_only' ? null : subjectTrim || null,
      body_html:
        contentMode === 'manual' || contentMode === 'template_wrap'
          ? bodyTrim || null
          : null,
      postmark_template_alias: contentMode === 'manual' ? null : aliasTrim || null,
      active,
      personalization_config: {
        enabled: contentMode !== 'template_only' && aiPersonalizationEnabled,
        min_score: aiMinScore,
      },
    };
  }, [
    automationId,
    position,
    dayOffset,
    timeOfDay,
    name,
    subject,
    bodyHtml,
    templateAlias,
    contentMode,
    active,
    aiPersonalizationEnabled,
    aiMinScore,
  ]);

  const persistStep = useCallback(
    async (options: { silent?: boolean } = {}) => {
      if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(timeOfDay)) {
        return false;
      }
      if (!Number.isFinite(dayOffset) || dayOffset < 0) {
        return false;
      }
      if (persistInFlightRef.current) {
        return false;
      }

      const payload = buildPayload();
      persistInFlightRef.current = true;
      setSaving(true);
      setSaveStatus('saving');
      setSaveError(null);

      try {
        let savedId = stepId;
        if (stepId) {
          const { automation_id: _omit, position: _position, ...patch } = payload;
          void _omit;
          void _position;
          const { error } = await supabase
            .from('automation_steps')
            .update(patch)
            .eq('id', stepId);
          if (error) throw error;
        } else {
          const { data, error } = await supabase
            .from('automation_steps')
            .insert(payload)
            .select('id')
            .single();
          if (error) throw error;
          savedId = data.id;
          setPersistedStepId(data.id);
        }

        lastSavedSnapshotRef.current = currentSnapshot;
        setSaveStatus('saved');
        onSuccess(savedId ?? undefined);
        if (!options.silent) {
          toast.success(stepId ? 'Step saved' : 'Step added');
        }
        return true;
      } catch (e: unknown) {
        const message = formatSupabaseError(e, 'Save failed');
        setSaveStatus('error');
        setSaveError(message);
        if (!options.silent) {
          toast.error(message);
        }
        return false;
      } finally {
        persistInFlightRef.current = false;
        setSaving(false);
      }
    },
    [buildPayload, currentSnapshot, dayOffset, onSuccess, stepId, timeOfDay],
  );

  const flushAutoSave = useCallback(async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    while (persistInFlightRef.current) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    if (currentSnapshot === lastSavedSnapshotRef.current) {
      return true;
    }
    return persistStep({ silent: true });
  }, [currentSnapshot, persistStep]);

  const handlePropagatePendingSends = useCallback(async () => {
    if (!stepId) return;
    setPropagating(true);
    try {
      const saved = await flushAutoSave();
      if (!saved) {
        toast.error('Save the step before updating pending sends');
        return;
      }

      const { data, error } = await supabase.rpc('propagate_automation_step_to_pending_sends', {
        p_step_id: stepId,
      });
      if (error) throw error;

      const count = typeof data === 'number' ? data : Number(data) || 0;
      setPropagateConfirmOpen(false);
      await loadPendingSendCount();
      onSuccess();
      if (count > 0) {
        toast.success(
          count === 1 ? 'Updated 1 pending send' : `Updated ${count} pending sends`,
        );
      } else {
        toast.message('No pending sends were updated');
      }
    } catch (e: unknown) {
      toast.error(formatSupabaseError(e, 'Could not update pending sends'));
    } finally {
      setPropagating(false);
    }
  }, [flushAutoSave, loadPendingSendCount, onSuccess, stepId]);

  const handleOpenPropagateConfirm = useCallback(async () => {
    if (!stepId || propagating) return;
    const saved = await flushAutoSave();
    if (!saved) {
      toast.error('Fix validation errors before updating pending sends');
      return;
    }
    await loadPendingSendCount();
    setPropagateConfirmOpen(true);
  }, [flushAutoSave, loadPendingSendCount, propagating, stepId]);

  useEffect(() => {
    if (!isOpen || !formHydrated) return;
    if (currentSnapshot === lastSavedSnapshotRef.current) return;

    setSaveStatus('pending');
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setTimeout(() => {
      autoSaveTimerRef.current = null;
      void persistStep({ silent: true });
    }, AUTO_SAVE_DELAY_MS);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
    };
  }, [currentSnapshot, formHydrated, isOpen, persistStep]);

  const usesTemplate =
    contentMode === 'template_wrap' || contentMode === 'template_only';
  const showsSubjectBody =
    contentMode === 'manual' || contentMode === 'template_wrap';

  const canPreview = useMemo(() => {
    const bodyTrim = bodyHtml.trim();
    const aliasTrim = templateAlias.trim();
    if (contentMode === 'manual') return !!bodyTrim;
    if (contentMode === 'template_wrap') return !!bodyTrim && !!aliasTrim;
    if (contentMode === 'template_only') return !!aliasTrim;
    return false;
  }, [bodyHtml, contentMode, templateAlias]);

  useEffect(() => {
    if (!isOpen) return;
    if (!showsSubjectBody && editorViewport === 'edit') {
      setEditorViewport('desktop');
    }
  }, [editorViewport, isOpen, setEditorViewport, showsSubjectBody]);

  useEffect(() => {
    if (!isOpen) return;
    void loadTemplates();
  }, [isOpen, loadTemplates]);

  useEffect(() => {
    if (!isOpen) {
      setDeleteConfirmOpen(false);
    }
  }, [isOpen]);

  const selectedTemplate = useMemo(() => {
    const v = templateAlias.trim();
    if (!v) return null;
    return (
      templates.find((t) => String(t.templateId) === v || t.alias === v) ?? null
    );
  }, [templates, templateAlias]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving && !deleting && !propagating && !pickerOpen) {
        if (propagateConfirmOpen) {
          setPropagateConfirmOpen(false);
          return;
        }
        if (deleteConfirmOpen) return;
        void flushAutoSave().finally(() => onClose());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    isOpen,
    onClose,
    saving,
    deleting,
    propagating,
    pickerOpen,
    deleteConfirmOpen,
    propagateConfirmOpen,
    flushAutoSave,
  ]);

  const handleClose = useCallback(async () => {
    if (saving || deleting) return;
    await flushAutoSave();
    onClose();
  }, [deleting, flushAutoSave, onClose, saving]);

  const selectedContentModeOption = useMemo(
    () => CONTENT_MODE_OPTIONS.find((o) => o.value === contentMode),
    [contentMode],
  );

  const isPreviewing = editorViewport === 'desktop' || editorViewport === 'mobile';

  const selectViewport = (viewport: EditorViewport) => {
    if ((viewport === 'desktop' || viewport === 'mobile') && !canPreview) return;
    setEditorViewport(viewport);
  };

  const loadPreview = useCallback(async () => {
    if (!canPreview) return;
    const requestId = ++previewRequestIdRef.current;
    setPreviewLoading(true);
    try {
      const result = await fetchScheduledEmailPreview({
        subject,
        bodyHtml: showsSubjectBody ? bodyHtml : null,
        postmarkTemplateAlias: usesTemplate ? templateAlias : null,
        contentMode,
      });
      if (requestId !== previewRequestIdRef.current) return;
      setPreviewSubject(result.subject);
      setPreviewHtml(result.html);
      setPreviewWarning(result.warning ?? null);
    } catch (e: unknown) {
      if (requestId !== previewRequestIdRef.current) return;
      if (showsSubjectBody) setEditorViewport('edit');
      toast.error(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      if (requestId === previewRequestIdRef.current) {
        setPreviewLoading(false);
      }
    }
  }, [bodyHtml, canPreview, contentMode, showsSubjectBody, subject, templateAlias, usesTemplate]);

  useEffect(() => {
    if (!isOpen || !isPreviewing) return;
    if (!canPreview) {
      if (showsSubjectBody) setEditorViewport('edit');
      return;
    }
    const timer = window.setTimeout(() => {
      void loadPreview();
    }, 350);
    return () => window.clearTimeout(timer);
  }, [canPreview, isOpen, isPreviewing, loadPreview, showsSubjectBody]);

  const validateBeforeSend = (): string | null => {
    const subjectTrim = subject.trim();
    const bodyTrim = bodyHtml.trim();
    const aliasTrim = templateAlias.trim();

    if (contentMode === 'manual') {
      if (!subjectTrim) return 'Subject is required for manual content';
      if (!bodyTrim) return 'Body is required for manual content';
    } else if (contentMode === 'template_wrap') {
      if (!aliasTrim) return 'Choose a Postmark template for wrapper mode';
      if (!bodyTrim) return 'Body HTML is required for wrapper mode';
    } else if (contentMode === 'template_only') {
      if (!aliasTrim) return 'Choose a Postmark template for template-only mode';
    }
    if (!/^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(timeOfDay)) {
      return 'Time must be HH:MM (24h)';
    }
    if (!Number.isFinite(dayOffset) || dayOffset < 0) {
      return 'Day offset must be 0 or greater';
    }
    return null;
  };

  const handleDone = async () => {
    const validationError = validateBeforeSend();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    const ok = await flushAutoSave();
    if (ok === false) return;
    onClose();
  };

  const handleDelete = async () => {
    if (!existing) return;
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('automation_steps')
        .delete()
        .eq('id', existing.id);
      if (error) throw error;
      setDeleteConfirmOpen(false);
      toast.success('Step deleted');
      onSuccess();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const stepLabel =
    name.trim() ||
    subject.trim() ||
    existing?.name ||
    existing?.subject ||
    (existing ? `Step ${existing.position}` : stepId ? `Step ${position}` : 'this step');

  const headerTitle = useMemo(() => {
    const trimmedName = name.trim();
    if (trimmedName) return trimmedName;
    if (!isEdit && !stepId) return 'Add step';
    const trimmedSubject = subject.trim();
    if (trimmedSubject) return trimmedSubject;
    if (existing?.position != null) return `Step ${existing.position}`;
    return 'Edit step';
  }, [name, subject, isEdit, stepId, existing?.position]);

  const currentStepIndex = useMemo(() => {
    if (!stepId || !allSteps?.length) return -1;
    return allSteps.findIndex((s) => s.id === stepId);
  }, [allSteps, stepId]);

  const showStepNav =
    allSteps != null &&
    allSteps.length > 1 &&
    currentStepIndex >= 0 &&
    onNavigateStep != null;

  const handleNavigateStep = useCallback(
    async (direction: 'prev' | 'next') => {
      if (!showStepNav || !allSteps || currentStepIndex < 0 || !onNavigateStep) return;
      const target =
        direction === 'prev' ? allSteps[currentStepIndex - 1] : allSteps[currentStepIndex + 1];
      if (!target) return;

      const saved = await flushAutoSave();
      if (!saved) {
        toast.error('Fix errors before switching steps');
        return;
      }
      await onNavigateStep(target.id);
    },
    [allSteps, currentStepIndex, flushAutoSave, onNavigateStep, showStepNav],
  );

  const saveStatusLabel = (() => {
    switch (saveStatus) {
      case 'pending':
        return 'Unsaved changes';
      case 'saving':
        return 'Saving…';
      case 'saved':
        return 'Saved';
      case 'error':
        return saveError ?? 'Save failed';
      default:
        return null;
    }
  })();

  if (!isOpen) return null;
  const busy = saving || deleting || propagating;

  const viewportToggleClass = (viewport: EditorViewport) =>
    [
      'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
      editorViewport === viewport
        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
    ].join(' ');

  const templateFieldControls = (
    <>
      <div
        className={`px-3 py-2 border rounded-lg text-sm flex items-center gap-2 min-h-[38px] ${
          templateAlias.trim()
            ? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/40 dark:bg-emerald-900/10'
            : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/30'
        }`}
      >
        {templateAlias.trim() ? (
          <>
            <FileCode className="w-4 h-4 text-emerald-500 shrink-0" />
            <div className="min-w-0 flex-1">
              {selectedTemplate ? (
                <>
                  <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {selectedTemplate.name || '(unnamed template)'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
                    id: {selectedTemplate.templateId}
                    {selectedTemplate.alias ? ` · alias: ${selectedTemplate.alias}` : ''}
                  </div>
                </>
              ) : templatesLoading ? (
                <span className="text-gray-500 dark:text-gray-400">
                  Looking up template <span className="font-mono">{templateAlias}</span>…
                </span>
              ) : (
                <span className="text-gray-700 dark:text-gray-200 font-mono break-all">
                  {templateAlias}{' '}
                  <span className="text-xs text-amber-600 dark:text-amber-400 font-sans">
                    (not found in Postmark)
                  </span>
                </span>
              )}
            </div>
          </>
        ) : (
          <span className="text-gray-400 dark:text-gray-500 italic">No template selected</span>
        )}
      </div>
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        className="mt-2 w-full px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
      >
        {templateAlias.trim() ? 'Change template' : 'Choose template'}
      </button>
      {templatesError ? (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
          Could not load templates: {templatesError}
        </p>
      ) : null}
    </>
  );

  const templateField = (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        Postmark template <span className="text-red-500">*</span>
      </label>
      {templateFieldControls}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-gray-800"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <div className="min-w-0">
          <h2
            className="text-lg font-semibold text-gray-900 dark:text-white truncate"
            title={headerTitle}
          >
            {headerTitle}
          </h2>
          {saveStatusLabel ? (
            <p
              className={`text-xs mt-0.5 truncate ${
                saveStatus === 'error'
                  ? 'text-red-600 dark:text-red-400'
                  : saveStatus === 'saved'
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {saveStatusLabel}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {showStepNav ? (
            <div
              className="flex items-center gap-0.5 mr-1"
              role="navigation"
              aria-label="Step navigation"
            >
              <Tooltip label="Previous step" side="bottom">
                <button
                  type="button"
                  onClick={() => void handleNavigateStep('prev')}
                  disabled={busy || currentStepIndex <= 0}
                  aria-label="Previous step"
                  className="inline-flex items-center justify-center p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              </Tooltip>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300 tabular-nums whitespace-nowrap px-1 min-w-[4.5rem] text-center">
                {currentStepIndex + 1} of {allSteps!.length}
              </span>
              <Tooltip label="Next step" side="bottom">
                <button
                  type="button"
                  onClick={() => void handleNavigateStep('next')}
                  disabled={busy || currentStepIndex >= allSteps!.length - 1}
                  aria-label="Next step"
                  className="inline-flex items-center justify-center p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </Tooltip>
            </div>
          ) : null}
          <button
            onClick={() => void handleClose()}
            disabled={busy}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
          <aside className="w-[26rem] shrink-0 overflow-y-auto border-r border-gray-200 dark:border-gray-700 px-6 py-5 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Welcome, Day 3 nudge"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                autoFocus
              />
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Internal label — subscribers never see it.
              </p>
            </div>

            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {active ? 'Active' : 'Inactive'}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Off skips this step for new enrollments only.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-10 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer-checked:bg-emerald-500 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-4" />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Day offset
                </label>
                <input
                  type="number"
                  min={0}
                  value={dayOffset}
                  onChange={(e) => setDayOffset(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Time
                </label>
                <input
                  type="time"
                  value={timeOfDay}
                  onChange={(e) => setTimeOfDay(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <select
                value={contentMode}
                onChange={(e) => setContentMode(e.target.value as AutomationStepContentMode)}
                title={selectedContentModeOption?.description}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {CONTENT_MODE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value} title={opt.description}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {selectedContentModeOption ? (
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  {selectedContentModeOption.description}
                </p>
              ) : null}
            </div>

            {usesTemplate ? templateField : null}

            {showsSubjectBody ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Subject{' '}
                  {contentMode === 'manual' ? (
                    <span className="text-red-500">*</span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                  )}
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            ) : null}

            {contentMode !== 'template_only' ? (
              <div>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Personalize
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Light LLM pass when the profile score meets the minimum.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={aiPersonalizationEnabled}
                      onChange={(e) => setAiPersonalizationEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer-checked:bg-emerald-500 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-4" />
                  </label>
                </div>
                {aiPersonalizationEnabled ? (
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-sm text-gray-600 dark:text-gray-300">Min score</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={aiMinScore}
                      onChange={(e) =>
                        setAiMinScore(Math.max(0, Math.min(100, Number(e.target.value) || 0)))
                      }
                      className="w-16 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                ) : null}
              </div>
            ) : null}

            {stepId ? (
              <div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Pending sends
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {pendingSendCountLoading ? (
                    'Checking queue…'
                  ) : pendingSendCount != null ? (
                    pendingSendCount === 1 ? (
                      '1 pending send linked to this step'
                    ) : (
                      `${pendingSendCount} pending sends linked to this step`
                    )
                  ) : (
                    'Could not load pending send count'
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => void handleOpenPropagateConfirm()}
                  disabled={
                    busy ||
                    !active ||
                    pendingSendCountLoading ||
                    pendingSendCount == null ||
                    pendingSendCount === 0
                  }
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {propagating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Update pending sends
                </button>
                {!active ? (
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
                    Turn Active on to update the queue from this step.
                  </p>
                ) : null}
              </div>
            ) : null}
          </aside>

          <div className="flex-1 min-h-0 flex flex-col items-center bg-gray-50/70 dark:bg-gray-900/40 px-6 py-5 sm:px-10 overflow-hidden">
            <div className="flex items-center justify-between w-full max-w-3xl mb-3 shrink-0">
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {isPreviewing || !showsSubjectBody ? (
                    'Send preview'
                  ) : (
                    <>
                      Body <span className="text-red-500">*</span>
                    </>
                  )}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {isPreviewing || !showsSubjectBody
                    ? previewSubject
                      ? `Subject: ${previewSubject}`
                      : 'Rendered as it would send.'
                    : <>Markdown or HTML — converted at send time. Use {'{{first_name}}'} for merge fields.</>}
                </p>
              </div>
              <div
                className="inline-flex items-center gap-0.5 p-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100/80 dark:bg-gray-800/80"
                role="group"
                aria-label="Editor viewport"
              >
                {showsSubjectBody ? (
                  <button
                    type="button"
                    onClick={() => selectViewport('edit')}
                    aria-pressed={editorViewport === 'edit'}
                    className={viewportToggleClass('edit')}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Edit
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => selectViewport('desktop')}
                  aria-pressed={editorViewport === 'desktop'}
                  disabled={!canPreview}
                  className={viewportToggleClass('desktop')}
                >
                  {previewLoading && editorViewport === 'desktop' ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Monitor className="w-3.5 h-3.5" />
                  )}
                  Desktop
                </button>
                <button
                  type="button"
                  onClick={() => selectViewport('mobile')}
                  aria-pressed={editorViewport === 'mobile'}
                  disabled={!canPreview}
                  className={viewportToggleClass('mobile')}
                >
                  {previewLoading && editorViewport === 'mobile' ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Smartphone className="w-3.5 h-3.5" />
                  )}
                  Mobile
                </button>
              </div>
            </div>

            <div
              className={`flex flex-col flex-1 min-h-0 w-full transition-[width,max-width] duration-200 ${
                editorViewport === 'mobile'
                  ? 'max-w-[390px]'
                  : 'max-w-3xl'
              }`}
            >
              <div
                className={`flex flex-col flex-1 min-h-0 overflow-hidden ${
                  editorViewport === 'mobile'
                    ? 'rounded-[2rem] border-[10px] border-gray-800 dark:border-gray-950 bg-gray-800 dark:bg-gray-950 shadow-2xl'
                    : 'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm'
                }`}
              >
                {editorViewport === 'mobile' ? (
                  <div className="h-7 shrink-0 flex items-center justify-center bg-gray-800 dark:bg-gray-950">
                    <div className="w-20 h-1.5 rounded-full bg-gray-600 dark:bg-gray-700" />
                  </div>
                ) : editorViewport === 'desktop' ? (
                  <div className="h-8 shrink-0 flex items-center gap-1.5 px-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-t-xl">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                    <span className="ml-2 text-[10px] text-gray-400 dark:text-gray-500 truncate">
                      Send preview
                    </span>
                  </div>
                ) : null}
                {isPreviewing ? (
                  <div
                    className={`flex flex-col flex-1 min-h-0 bg-white ${
                      editorViewport === 'mobile' ? 'rounded-b-[1.4rem]' : 'rounded-b-xl'
                    }`}
                  >
                    {previewWarning ? (
                      <p className="shrink-0 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 px-3 py-2">
                        {previewWarning}
                      </p>
                    ) : null}
                    {previewLoading && !previewHtml ? (
                      <div className="flex flex-1 items-center justify-center">
                        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                      </div>
                    ) : previewHtml ? (
                      <iframe
                        title="Send preview"
                        srcDoc={previewHtml}
                        sandbox="allow-same-origin"
                        className="flex-1 min-h-0 w-full border-0 bg-white"
                      />
                    ) : (
                      <p className="flex flex-1 items-center justify-center text-sm text-gray-500 dark:text-gray-400 px-6 text-center">
                        Nothing to preview yet.
                      </p>
                    )}
                  </div>
                ) : (
                  <textarea
                    value={bodyHtml}
                    onChange={(e) => setBodyHtml(e.target.value)}
                    className="flex-1 min-h-0 w-full px-4 py-3 border-0 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm font-mono leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-inset focus:ring-emerald-500 rounded-xl"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

      <div className="flex justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2">
            {isEdit ? (
              <Tooltip label="Delete step" side="bottom">
                <button
                  type="button"
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={busy}
                  aria-label="Delete step"
                  className="inline-flex items-center justify-center p-2 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 rounded-lg transition-colors"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
              </Tooltip>
            ) : null}
            {isEdit && onDuplicate ? (
              <Tooltip label="Duplicate step" side="bottom">
                <button
                  type="button"
                  onClick={onDuplicate}
                  disabled={busy}
                  aria-label="Duplicate step"
                  className="inline-flex items-center justify-center p-2 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </Tooltip>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => void handleClose()}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => void handleDone()}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {saving ? 'Saving…' : 'Done'}
            </button>
          </div>
        </div>

      <PostmarkTemplatePickerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        templates={templates}
        loading={templatesLoading}
        error={templatesError}
        onRefresh={() => void loadTemplates()}
        currentValue={templateAlias}
        onSelect={(t) => setTemplateAlias(String(t.templateId))}
      />

      <ConfirmModal
        isOpen={propagateConfirmOpen}
        title="Update pending sends?"
        description={
          <>
            Copy the saved content from{' '}
            <span className="font-medium text-gray-900 dark:text-white">{stepLabel}</span> onto{' '}
            {pendingSendCount === 1 ? (
              <>1 pending scheduled send</>
            ) : (
              <>{pendingSendCount ?? 0} pending scheduled sends</>
            )}
            . Send times stay the same. AI personalization on those rows will reset and may run
            again before send.
          </>
        }
        confirmLabel="Update sends"
        busy={propagating}
        onConfirm={() => void handlePropagatePendingSends()}
        onClose={() => {
          if (!propagating) setPropagateConfirmOpen(false);
        }}
      />

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        title="Delete step?"
        description={
          <>
            Permanently remove{' '}
            <span className="font-medium text-gray-900 dark:text-white">{stepLabel}</span> from this
            automation. Pending scheduled sends for this step will be removed. This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        iconOnlyConfirm
        variant="danger"
        busy={deleting}
        onConfirm={() => void handleDelete()}
        onClose={() => {
          if (!deleting) setDeleteConfirmOpen(false);
        }}
      />
    </div>
  );
}
